import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowDownTrayIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import {
  createExportJob,
  getExportJobStatus,
  downloadExportFile,
  deleteExportJob,
  formatFileSize,
  ExportJob,
} from '../services/exportService';
import { useAuth } from '../contexts/AuthContext';

interface ExportButtonProps {
  exportType: string;
  filters: Record<string, any>;
  columns?: { key: string; header: string }[];
  disabled?: boolean;
  className?: string;
  totalRows?: number; // Used for display purposes (optional)
}

const ExportButton: React.FC<ExportButtonProps> = ({
  exportType,
  filters,
  columns,
  disabled = false,
  className = '',
}) => {
  const { user } = useAuth();
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<ExportJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Get user preferences with defaults
  const userPreferences = useMemo(() => ({
    userTimezone: user?.preferences?.timezone || 'Asia/Kolkata',
    userDateFormat: user?.preferences?.dateFormat || 'MM/dd/yyyy',
    userTimeFormat: user?.preferences?.timeFormat || '24H',
  }), [user?.preferences]);

  // Poll for job status
  useEffect(() => {
    if (!jobId) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await getExportJobStatus(jobId);
        if (status) {
          setJob(status);
          if (status.status === 'COMPLETED' || status.status === 'FAILED' || status.status === 'EXPIRED') {
            clearInterval(pollInterval);
          }
        }
      } catch (err) {
        console.error('Failed to get export status:', err);
      }
    }, 1000); // Poll every second

    return () => clearInterval(pollInterval);
  }, [jobId]);

  const handleStartExport = useCallback(async () => {
    setError(null);
    setJob(null);
    setShowModal(true);

    try {
      // Include user preferences in filters for date formatting
      const filtersWithPreferences = {
        ...filters,
        ...userPreferences,
      };
      const newJobId = await createExportJob({
        exportType,
        filters: filtersWithPreferences,
        columns,
      });
      setJobId(newJobId);
    } catch (err: any) {
      setError(err.message || 'Failed to start export');
    }
  }, [exportType, filters, columns, userPreferences]);

  const handleClose = useCallback(async (skipCleanup = false) => {
    // Cleanup: if job completed but user closed without downloading, delete the file
    if (!skipCleanup && jobId && job?.status === 'COMPLETED') {
      try {
        await deleteExportJob(jobId);
      } catch (cleanupErr) {
        // Silently ignore cleanup errors
        console.warn('Failed to cleanup export file:', cleanupErr);
      }
    }
    setShowModal(false);
    setJobId(null);
    setJob(null);
    setError(null);
  }, [jobId, job]);

  const handleDownload = useCallback(async () => {
    if (!job?.fileName || !jobId) return;

    try {
      await downloadExportFile(jobId, job.fileName, job.downloadUrl);
      // DON'T delete immediately - the browser download is async and needs the blob to exist
      // The file will be cleaned up by:
      // 1. Expiry job (after 24 hours)
      // 2. When user closes modal without downloading
      // Close modal after initiating download (skip cleanup to let download complete)
      handleClose(true);
    } catch (err: any) {
      setError(err.message || 'Failed to download file');
    }
  }, [job, jobId, handleClose]);

  const renderProgressContent = () => {
    if (error) {
      return (
        <div className="text-center">
          <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-600 font-medium">Export Failed</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      );
    }

    if (!job) {
      return (
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-3"></div>
          <p className="text-gray-600">Starting export...</p>
        </div>
      );
    }

    if (job.status === 'PENDING') {
      return (
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-3"></div>
          <p className="text-gray-600">Queued for processing...</p>
        </div>
      );
    }

    if (job.status === 'PROCESSING') {
      return (
        <div className="text-center">
          <div className="relative w-48 mx-auto mb-3">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 transition-all duration-300"
                style={{ width: `${job.progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-1">{job.progress}%</p>
          </div>
          <p className="text-gray-600">
            Processing {job.processedRows?.toLocaleString() || 0} of {job.totalRows?.toLocaleString() || '?'} rows...
          </p>
          <p className="text-xs text-gray-400 mt-2">This may take a few minutes for large exports</p>
        </div>
      );
    }

    if (job.status === 'COMPLETED') {
      return (
        <div className="text-center">
          <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-green-600 font-medium">Export Complete!</p>
          <p className="text-sm text-gray-500 mt-1">
            {job.totalRows?.toLocaleString()} rows exported
            {job.fileSize && ` (${formatFileSize(job.fileSize)})`}
          </p>
          <button
            onClick={handleDownload}
            className="mt-4 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
            Download CSV
          </button>
          {job.expiresAt && (
            <p className="text-xs text-gray-400 mt-3">
              Download link expires: {new Date(job.expiresAt).toLocaleString()}
            </p>
          )}
        </div>
      );
    }

    if (job.status === 'FAILED') {
      return (
        <div className="text-center">
          <XCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-600 font-medium">Export Failed</p>
          <p className="text-sm text-gray-500 mt-1">{job.errorMessage || 'Unknown error'}</p>
        </div>
      );
    }

    if (job.status === 'EXPIRED') {
      return (
        <div className="text-center">
          <XCircleIcon className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
          <p className="text-yellow-600 font-medium">Download Expired</p>
          <p className="text-sm text-gray-500 mt-1">Please start a new export</p>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <button
        onClick={handleStartExport}
        disabled={disabled}
        className={`inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
        Export CSV
      </button>

      {/* Export Progress Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
              onClick={job?.status === 'COMPLETED' || job?.status === 'FAILED' || error ? () => handleClose() : undefined}
            ></div>

            {/* Modal */}
            <div className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
                Export Progress
              </h3>

              {renderProgressContent()}

              {/* Close button (only show when can close) */}
              {(job?.status === 'COMPLETED' || job?.status === 'FAILED' || job?.status === 'EXPIRED' || error) && (
                <button
                  onClick={() => handleClose()}
                  className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-700"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExportButton;
